<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const currentStep = ref(1);
const inboxName = ref('');
const consentAccepted = ref(false);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const canCreate = computed(
  () => inboxName.value.trim().length > 0 && consentAccepted.value
);

function goToSetup() {
  if (consentAccepted.value) currentStep.value = 2;
}

async function createChannel() {
  if (!canCreate.value) return;

  try {
    const channel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: { type: 'dinastia_whatsapp' },
    });
    // Go directly to QR/Finish step — QR codes expire in ~20s so showing ASAP is critical
    // Agents can be added later in inbox settings
    router.replace({
      name: 'settings_inbox_finish',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.ERROR_MESSAGE'));
  }
}
</script>

<template>
  <div class="flex flex-col max-w-xl">
    <!-- Step 1: Consent -->
    <div v-if="currentStep === 1" class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center size-11 rounded-xl bg-n-teal-3"
        >
          <span class="i-ri-whatsapp-fill text-2xl text-n-teal-11" />
        </div>
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mb-0">
            {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.DESC') }}
          </p>
        </div>
      </div>

      <div class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-5">
        <div class="flex gap-3">
          <span
            class="i-lucide-triangle-alert text-lg text-n-amber-11 shrink-0 mt-0.5"
          />
          <div class="flex flex-col gap-3">
            <p class="text-sm text-n-amber-12 leading-relaxed mb-0">
              {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.CONSENT_TEXT') }}
            </p>
            <label class="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                v-model="consentAccepted"
                type="checkbox"
                class="size-4 rounded border-n-weak"
              />
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.CONSENT_CHECKBOX') }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <NextButton
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.CONTINUE_BUTTON')"
        :disabled="!consentAccepted"
        @click="goToSetup"
      />
    </div>

    <!-- Step 2: Name & Create -->
    <div v-else class="flex flex-col gap-6">
      <button
        type="button"
        class="flex items-center gap-1.5 text-sm text-n-slate-10 hover:text-n-slate-12 transition-colors w-fit"
        @click="currentStep = 1"
      >
        <span class="i-lucide-arrow-left text-base" />
        {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.BACK_BUTTON') }}
      </button>

      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center size-11 rounded-xl bg-n-teal-3"
        >
          <span class="i-ri-whatsapp-fill text-2xl text-n-teal-11" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.SETUP_TITLE') }}
        </h3>
      </div>

      <form class="flex flex-col gap-5" @submit.prevent="createChannel">
        <div class="flex flex-col gap-1.5">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="dinastia-inbox-name"
          >
            {{ $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.INBOX_NAME.LABEL') }}
          </label>
          <input
            id="dinastia-inbox-name"
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.INBOX_NAME.PLACEHOLDER')
            "
          />
        </div>

        <NextButton
          solid
          blue
          type="submit"
          :label="$t('INBOX_MGMT.ADD.DINASTIA_WHATSAPP.SUBMIT_BUTTON')"
          :is-loading="uiFlags.isCreating"
          :disabled="!canCreate"
        />
      </form>
    </div>
  </div>
</template>
